// Cookie-consent state (Spec M13.2). The `ddm_consent` cookie is the source of
// truth for gating GA4 (analytics) / Meta Pixel (marketing). Essential cookies
// (session, auth, reCAPTCHA) are always on and never stored here as a choice.

export type ConsentChoice = {
  v: 1;
  essential: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

const COOKIE_NAME = 'ddm_consent';
// 13 months (Spec M13.2). 395 days keeps it just over a year per CNIL guidance.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 395;
const CONSENT_CHANGED_EVENT = 'ddm:consent-changed';
const OPEN_CONSENT_EVENT = 'ddm:open-consent';

export function getConsent(): ConsentChoice | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${COOKIE_NAME}=`))
    ?.slice(COOKIE_NAME.length + 1);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<ConsentChoice>;
    if (parsed && parsed.v === 1) {
      return {
        v: 1,
        essential: true,
        analytics: Boolean(parsed.analytics),
        marketing: Boolean(parsed.marketing),
        ts: typeof parsed.ts === 'number' ? parsed.ts : 0,
      };
    }
  } catch {
    // Malformed cookie → treat as no decision (banner re-shows).
  }
  return null;
}

export function hasDecision(): boolean {
  return getConsent() !== null;
}

export function setConsent(analytics: boolean, marketing: boolean): ConsentChoice {
  const choice: ConsentChoice = {
    v: 1,
    essential: true,
    analytics,
    marketing,
    ts: Date.now(),
  };
  if (typeof document !== 'undefined') {
    const value = encodeURIComponent(JSON.stringify(choice));
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGED_EVENT, { detail: choice }));
  }
  return choice;
}

export function onConsentChanged(handler: (choice: ConsentChoice) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<ConsentChoice>).detail);
  window.addEventListener(CONSENT_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, listener);
}

// Footer "Cookie preferences" re-opens the banner via this event.
export function openConsentPreferences(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT));
  }
}

export function onOpenConsent(handler: () => void): () => void {
  const listener = () => handler();
  window.addEventListener(OPEN_CONSENT_EVENT, listener);
  return () => window.removeEventListener(OPEN_CONSENT_EVENT, listener);
}
